import type { HotkeyToken } from '@core/hotkey/tokens';
import type { Placement } from '@floating-ui/dom';
import {
  type ButtonRootProps,
  Button as KobalteButton,
} from '@kobalte/core/button';
import { themeReactive } from '@theme/signals/themeReactive';
import { type ComponentProps, type JSX, Show, splitProps } from 'solid-js';
import { cn } from '../utils/classname';
import { useButtonGroupContext } from './ButtonGroup';
import { Layer } from './Layer';
import { Tooltip } from './Tooltip';

export type ButtonProps = ButtonRootProps<'button'> &
  ComponentProps<'button'> & {
    depth?: 0 | 1 | 2 | 3 | 4 | 5;
    tooltipPlacement?: Placement;
    /**
     * Stretch the button (and, when a tooltip wraps it, the tooltip's trigger
     * wrapper) to fill the available width. Without this the tooltip wrapper is
     * `inline-flex` and collapses a `w-full` button to its content width.
     */
    fullWidth?: boolean;
    noTouchResize?: boolean;
    variant?: ButtonVariant;
    children?: JSX.Element;
    tooltip?: string;
    label?: string;
    hotkey?: HotkeyToken | HotkeyToken[];
    /**
     * Raw shortcut string(s) shown in the tooltip when no `hotkey` token is available.
     */
    shortcut?: string | string[];
    size?: ButtonSize;
    class?: string;
    tooltipDisabled?: boolean;
  };

export type ButtonSize =
  | 'xs'
  | 'icon-xs'
  | 'sm'
  | 'icon-sm'
  | 'md'
  | 'icon-md'
  | 'lg'
  | 'icon-lg';

export type ButtonVariant =
  | 'ghost'
  | 'base'
  | 'active'
  | 'success'
  | 'danger'
  | 'contrast'
  | 'cta';

const variantStyles: Record<ButtonVariant, string> = {
  danger:
    'bg-transparent text-failure border border-failure/50 not-disabled:hover:bg-failure/10 not-disabled:active:bg-failure/20 disabled:opacity-30 ',
  base: 'bg-transparent text-ink-muted border border-edge-muted not-disabled:hover:bg-hover not-disabled:hover:text-ink active:bg-active disabled:opacity-30 ',
  active:
    'bg-accent-bg not-disabled:hover:bg-accent/30 text-accent disabled:opacity-30 ',
  success:
    'bg-success-bg not-disabled:hover:bg-success/30 text-success disabled:opacity-30 ',
  ghost:
    'bg-transparent text-ink-muted not-disabled:hover:bg-hover not-disabled:hover:text-ink active:bg-active disabled:opacity-30 ',
  contrast:
    'bg-ink text-surface border border-transparent not-disabled:hover:bg-ink/90 active:bg-ink/80 disabled:opacity-30 focus-visible:bg-ink/90',
  cta: 'bg-accent text-surface border border-transparent not-disabled:hover:bg-accent/90 active:bg-accent/80 disabled:opacity-30 focus-visible:bg-accent/90',
};

const sizeStyles: Record<ButtonSize, string> = {
  xs: '          p-1  [&_:where(svg)]:size-3 gap-1   text-xs',
  'icon-xs': 'size-5    p-0.5  [&_:where(svg)]:size-4                  ',
  lg: '          p-2.5  [&_:where(svg)]:size-5 gap-2   text-base',
  md: '          p-2                           gap-1.5 text-sm  ' /* scuffed */,
  sm: 'h-6       px-2   [&_:where(svg)]:size-4 gap-1   text-xs  ',
  'icon-lg':
    'size-11   p-2    [&_:where(svg)]:size-7                  ' /* unused */,
  'icon-md': 'size-9    p-1.5  [&_:where(svg)]:size-6                  ',
  'icon-sm': 'size-6    p-0.5  [&_:where(svg)]:size-5                  ',
};

export const Button = (props: ButtonProps) => {
  const [local, others] = splitProps(props, [
    'tooltipPlacement',
    'children',
    'tooltip',
    'variant',
    'hotkey',
    'shortcut',
    'class',
    'depth',
    'label',
    'size',
    'fullWidth',
    'tooltipDisabled',
  ]);

  const group = useButtonGroupContext();

  const cls = () =>
    cn(
      'relative inline-flex items-center justify-center font-medium leading-none border border-transparent rounded-sm whitespace-nowrap',
      local.fullWidth && 'w-full',
      {
        'touch:min-h-9 touch:min-w-9 touch:[&_svg]:size-6':
          !props.noTouchResize,
      },
      'outline-none focus-visible:bg-active',
      'data-disabled:cursor-not-allowed',
      variantStyles[local.variant ?? group?.variant ?? 'ghost'],
      sizeStyles[local.size ?? group?.size ?? 'md'],
      local.class
    );

  const placement = () => local.tooltipPlacement ?? 'bottom';

  const variantStyle = (): JSX.CSSProperties | string | undefined => {
    const variant = local.variant ?? group?.variant;
    if (variant === 'cta') {
      // TODO (seamus): this is scuffed but better than what we had.
      const textL = themeReactive.a0.l[0]() < 0.72 ? 0.97 : 0.2;
      return {
        color: `oklch(${textL} var(--c0c) var(--c0h))`,
        '--color-edge': `oklch(${textL} var(--c0c) var(--c0h) / 0.7)`,
        '--color-edge-muted': `oklch(${textL} var(--c0c) var(--c0h) / 0.7)`,
      };
    }
    return others.style;
  };

  const button = () => (
    <KobalteButton data-button class={cls()} {...others} style={variantStyle()}>
      {local.children}
    </KobalteButton>
  );

  const tooltipLabel = () => local.label ?? local.tooltip;

  // Skip Layer when inside a ButtonGroup (the group already provides one)
  // unless the button has its own explicit depth
  const skipLayer = () => group !== undefined && local.depth === undefined;

  const content = () => (
    <Show
      when={tooltipLabel() !== undefined ? tooltipLabel() : false}
      fallback={button()}
    >
      {(label) => (
        <Tooltip
          class={local.fullWidth ? 'w-full' : undefined}
          hotkey={local.hotkey}
          shortcut={local.shortcut}
          placement={placement()}
          label={label()}
          disabled={local.tooltipDisabled}
        >
          {button()}
        </Tooltip>
      )}
    </Show>
  );

  return (
    <Show
      when={skipLayer()}
      fallback={<Layer depth={local.depth ?? 0}>{content()}</Layer>}
    >
      {content()}
    </Show>
  );
};
